import React from 'react';
import NumberFormat from 'react-number-format';
import { TextField } from '../TextField';
import { InputAdornment } from '../Input';
import { FormHelperText } from '../Form';
import styledMmmField from '../styledMmmField';

const StyledNumberFormat = styledMmmField(NumberFormat);

/*
A Final-Form compatible number input field that
restricts input to valid numbers, inserts commas
automatically, and adds a $ adornment for currency inputs
*/

function NumberField({
  input = {},
  meta = {},
  InputProps = {},
  helperText,
  required,
  valueType = 'number',
  decimalScale = 0,
  allowNegative = false,
  allowEmpty = false,
  ...custom
}) {
  const {
    name,
    type,
    onChange,
    value,
    color = 'primary',
    ...restInput
  } = input;
  const { touched, error } = meta;
  const hasError = Boolean(touched && error);
  const startAdornment = valueType === 'currency' ? <InputAdornment position='start'>$</InputAdornment> : undefined;

  const handleChange = (changeObj) => {
    // Set empty strings to 0 unless empty string is needed to unset in niche situations.
    if (!changeObj.value) {
      return allowEmpty ? onChange('') : onChange('0');
    }

    // If the user entered a '0' into a box that already had a zero, call onChange
    // with an empty string. This forces a re-render when the condition above executes,
    // which is needed to avoid the value prop going stale and the box adding a trailing
    // 0 to input
    if (changeObj.value.match(/^0{2,}$/)) {
      return onChange('');
    }

    // When entering a character before a single '0', replace the 0 with
    // that character
    if (value === '0' && changeObj.value.match(/^\d0$/)) {
      return onChange(changeObj.value[0]);
    }
    return onChange(changeObj.value);
  };

  return (
    <StyledNumberFormat
      customInput={TextField}
      decimalScale={decimalScale}
      onValueChange={handleChange}
      thousandSeparator=','
      error={hasError}
      helperText={hasError ? error : helperText}
      FormHelperTextProps={{ component: FormHelperText, error: hasError }}
      inputProps={{ required, ...restInput }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      InputProps={{ startAdornment, ...InputProps }}
      name={name}
      allowNegative={allowNegative}
      value={value}
      color={color}
      {...custom}
    />
  );
}

export default NumberField;
